import { useState, useEffect, useCallback } from 'react';

/**
 * Hook for voting power calculation
 */
export function useVotingPower(category = 'all') {
  const [votingPower, setVotingPower] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch voting power from backend
  const fetchVotingPower = useCallback(async () => {
    // Only fetch if user is logged in
    if (!window.t24Governance.isLoggedIn) {
      setVotingPower({ total_weight: 0, breakdown: { base: 0, type: 'guest' } });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        action: 't24_get_voting_power',
        category: category,
        nonce: window.t24Governance.nonce,
      });

      const response = await fetch(`${window.t24Governance.ajaxUrl}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch voting power');
      }

      const data = await response.json();

      if (data.success && data.data) {
        setVotingPower(data.data);
      } else {
        throw new Error(data.data?.message || 'Failed to load voting power');
      }
    } catch (err) {
      console.error('Error fetching voting power:', err);
      setError(err.message);
      // Default to base voting power on error
      setVotingPower({ total_weight: 1.0, breakdown: { base: 1.0, type: 'community_member' } });
    } finally {
      setLoading(false);
    }
  }, [category]);

  // Initial fetch
  useEffect(() => {
    fetchVotingPower();
  }, [fetchVotingPower]);

  // Refetch voting power
  const refetch = useCallback(() => {
    fetchVotingPower();
  }, [fetchVotingPower]);

  // Get formatted voting power display
  const getVotingPowerDisplay = useCallback(() => {
    if (!votingPower) return '0.0';
    return votingPower.total_weight.toFixed(1);
  }, [votingPower]);

  // Get role type label
  const getRoleTypeLabel = useCallback(() => {
    if (!votingPower || !votingPower.breakdown) return 'Guest';

    const breakdown = votingPower.breakdown;

    if (breakdown.role_type) {
      return breakdown.role_type;
    }

    if (breakdown.type === 'community_member') {
      return 'Community Member';
    }

    if (breakdown.type === 'guest') {
      return 'Guest';
    }

    return 'Member';
  }, [votingPower]);

  // Check if user has elevated voting power
  const hasElevatedPower = useCallback(() => {
    if (!votingPower) return false;
    return votingPower.total_weight > 1.0;
  }, [votingPower]);

  // Get voting power multiplier
  const getMultiplier = useCallback(() => {
    if (!votingPower) return 1.0;
    return votingPower.total_weight;
  }, [votingPower]);

  return {
    votingPower,
    loading,
    error,
    refetch,
    getVotingPowerDisplay,
    getRoleTypeLabel,
    hasElevatedPower,
    getMultiplier,
  };
}
